import { Injectable } from '@angular/core';
import { PassengerIdEntry } from '@app/utils/passenger-id.utils';

export interface PassengerIdMapEntry {
  passengerId: string;
  passengerIds: PassengerIdEntry[];
}

@Injectable({
  providedIn: 'root',
})
export class PassengerIdMapService {
  private readonly STORAGE_KEY = 'passengerIdMap';

  /**
   * Upserts passenger ID data into the session mapping.
   * Only updates entries where passengerIds has length > 0 and passengerId is truthy.
   */
  upsert(
    entries: {
      passengerNameNumber: string;
      passengerId: string;
      passengerIds: PassengerIdEntry[];
    }[]
  ): void {
    try {
      const current = this.loadMap();

      for (const entry of entries) {
        if (
          entry.passengerId &&
          Array.isArray(entry.passengerIds) &&
          entry.passengerIds.length > 0
        ) {
          current[entry.passengerNameNumber] = {
            passengerId: entry.passengerId,
            passengerIds: entry.passengerIds,
          };
        }
      }

      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(current));
    } catch (error) {
      console.warn('PassengerIdMapService: Failed to write to sessionStorage', error);
    }
  }

  /**
   * Retrieves the mapping entry for a given passengerNameNumber.
   */
  get(passengerNameNumber: string): PassengerIdMapEntry | null {
    try {
      const map = this.loadMap();
      return map[passengerNameNumber] || null;
    } catch (error) {
      console.warn('PassengerIdMapService: Failed to read from sessionStorage', error);
      return null;
    }
  }

  /**
   * Clears the entire mapping. Called by clearAll() on session end.
   */
  clear(): void {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('PassengerIdMapService: Failed to clear sessionStorage', error);
    }
  }

  /**
   * Returns the full map (for debugging/telemetry).
   */
  getAll(): Record<string, PassengerIdMapEntry> {
    try {
      return this.loadMap();
    } catch (error) {
      console.warn('PassengerIdMapService: Failed to read from sessionStorage', error);
      return {};
    }
  }

  private loadMap(): Record<string, PassengerIdMapEntry> {
    try {
      const raw = sessionStorage.getItem(this.STORAGE_KEY);
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return {};
      }
      return parsed;
    } catch (error) {
      // Malformed JSON or sessionStorage error — return empty map
      return {};
    }
  }
}
